import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Award } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/generals/modal'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { announcement } from '@/constants/home'

interface AnnouncementPillProps {
    className?: string
}

export const AnnouncementPill: FC<AnnouncementPillProps> = ({ className }): ReactNode => {
    const [open, setOpen] = useState(false)
    const id = useId()
    const { openContact } = useContact()

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                className={cn(
                    'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white/80 py-1.5 pl-1.5 pr-3.5 text-left text-[13px] font-medium text-graphite shadow-[0_1px_2px_rgba(14,14,14,0.04)] ring-1 ring-inset ring-[#e6e6ec] backdrop-blur transition-colors hover:bg-white hover:ring-[#d9d9e1] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet',
                    className
                )}
            >
                <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-violet px-2 py-[3px] text-[11px] font-semibold text-white'>
                    <Award className='size-3' aria-hidden='true' />
                    {announcement.badge}
                </span>

                <span className='truncate'>
                    {announcement.text}
                </span>

                <span aria-hidden='true' className='hidden h-3.5 w-px shrink-0 bg-[#e3e3e9] sm:block' />

                <span className='hidden shrink-0 items-center gap-1 text-ink sm:inline-flex'>
                    {announcement.link}
                    <ArrowRight className='size-3.5 transition-transform duration-300 group-hover:translate-x-0.5' aria-hidden='true' />
                </span>
            </button>

            <Modal open={open} onClose={() => setOpen(false)} labelledBy={`${id}-title`}>
                <div className='flex flex-col gap-6 px-6 pb-7 pt-9 sm:px-8 sm:pb-8'>
                    <span className='inline-flex w-fit items-center gap-1.5 rounded-full bg-violet-soft px-3 py-1 text-[12px] font-semibold text-violet'>
                        <Award className='size-3.5' aria-hidden='true' />
                        Deloitte Technology Fast 500
                    </span>

                    <div className='flex flex-col gap-3 pr-6'>
                        <h2 id={`${id}-title`} className='text-[26px] font-medium leading-tight tracking-[-0.03em]'>
                            FlowCraft ranks #212 on the Deloitte Fast 500
                        </h2>

                        <p className='text-[15px] leading-relaxed text-slate'>
                            FlowCraft placed #212 on this year&apos;s Deloitte Technology Fast 500, the ranking of the fastest-growing technology companies in North America, with 640% revenue growth over the measured period.
                        </p>

                        <p className='text-[15px] leading-relaxed text-slate'>
                            The growth comes from teams that hand us their hardest integrations and stay because the workflows keep running.
                        </p>
                    </div>

                    <dl className='grid grid-cols-2 gap-3'>
                        <div className='rounded-2xl bg-mist px-4 py-3.5'>
                            <dt className='text-[13px] text-slate'>Overall rank</dt>
                            <dd className='mt-1 text-[28px] font-medium tracking-[-0.03em]'>#212</dd>
                        </div>

                        <div className='rounded-2xl bg-mist px-4 py-3.5'>
                            <dt className='text-[13px] text-slate'>Revenue growth</dt>
                            <dd className='mt-1 text-[28px] font-medium tracking-[-0.03em]'>640%</dd>
                        </div>
                    </dl>

                    <PillButton
                        chevron
                        className='w-fit'
                        onClick={() => {
                            setOpen(false)
                            openContact({ intent: 'contact' })
                        }}
                    >
                        Talk to our team
                    </PillButton>
                </div>
            </Modal>
        </>
    )
}
